// packages/kb-view/src/markdown-view.tsx
//
// LAT-780 Slice 3 / LAT-1605 (LAT-1600 S5) — the read-only Markdown renderer
// shared by the in-app content pane and the public docs worker. Wraps
// react-markdown with GFM (tables, task lists, strikethrough) and Tailwind
// `prose` styling. Raw HTML is intentionally NOT enabled (no rehype-raw), so
// embedded HTML in KB content is ignored rather than executed — safe display of
// model- and agent-written memory without a separate sanitizer.
//
// This is the CORE: it renders bare anchors and knows nothing about knowledge
// bases, link verdicts, or evidence. Everything a surface wants on top arrives
// through two injection points:
//
//   - `remarkPlugins` — appended AFTER remark-gfm, in the caller's order;
//   - `components`    — handed straight to react-markdown.
//
// That is what keeps the package free of shadcn, Radix, and lucide: the
// application composes its evidence and link-verdict elements and passes them
// in, and a no-JS server-rendered surface pays for none of it.
//
// LAT-2081 (+ MEDIA, M6) — a THIRD injection point, `resolveImage`, because a
// picture is the one element a `components` override cannot serve on its own:
// both surfaces need the identical painting rules (lazy, alt from the
// markdown, bounded width, a marker instead of a browser broken-image icon)
// and differ only in where the bytes come from. So the painting lives here and
// the URL policy stays with the surface — the application maps a reference to
// the authenticated content route, the published surface to the public asset
// route (M12). See `./image-reference` for the seam and the path rule.

import {
  createContext,
  useContext,
  useState,
  type ComponentProps,
} from "react";
import ReactMarkdown, {
  defaultUrlTransform,
  type Components,
} from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "./cn";
import type {
  MarkdownImageResolution,
  MarkdownImageResolver,
} from "./image-reference";

type RemarkPlugins = ComponentProps<typeof ReactMarkdown>["remarkPlugins"];

/**
 * What a reader sees where a picture could not be shown: the path the page
 * asked for, in words. A browser's broken-image icon says only that something
 * failed; this says WHICH memory is missing, which is the thing a reader can
 * act on.
 *
 * Styled inline rather than with utility classes on purpose: Tailwind scans
 * each application's own sources, not this package's, so a class written here
 * would be compiled by neither consumer. The custom properties it reads are
 * defined by both surfaces (`styles/tokens.css`), and `data-slot` leaves a
 * hook for a surface that wants to restyle it.
 */
function BrokenImageReference({ alt, path }: { alt: string; path: string }) {
  const label = alt.trim();
  return (
    <span
      data-slot="broken-image-reference"
      title={`Image not found — ${path}`}
      style={{
        color: "var(--muted-foreground)",
        textDecorationLine: "underline",
        textDecorationStyle: "dotted",
        textUnderlineOffset: "0.2em",
      }}
    >
      {label ? `${label} — ` : ""}
      {`image not found: ${path}`}
    </span>
  );
}

/**
 * One resolved picture. Holds the only state in this file: whether THIS url
 * has already failed to load, so a rendition that is unavailable at fetch time
 * (ADR 0064 §3 produces them lazily, and can answer 503) degrades to the same
 * marker a missing file gets. Comparing against the url rather than a boolean
 * makes the reset free when the resolver returns a different one.
 */
function ResolvedImage({
  resolution,
  alt,
}: {
  resolution: Extract<MarkdownImageResolution, { kind: "image" }>;
  alt: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (failedUrl === resolution.url) {
    return <BrokenImageReference alt={alt} path={resolution.path} />;
  }
  return (
    <img
      src={resolution.url}
      alt={alt}
      loading="lazy"
      decoding="async"
      style={{ maxWidth: "100%", height: "auto" }}
      onError={() => setFailedUrl(resolution.url)}
    />
  );
}

/**
 * How the surface's resolver reaches the `img` override — through context, NOT
 * through a closure.
 *
 * react-markdown uses whatever sits at `components.img` as the element TYPE, so
 * a function minted per render is a NEW type and React replaces every picture
 * rather than re-rendering it. That discards {@link ResolvedImage}'s "this url
 * already failed" state, and with it the coordinator's rendition-unavailable
 * ruling: the marker would flash back to an `<img>` re-requesting the failing
 * url on the next parent render. And parent renders are routine — the
 * application rebuilds its `components` object every time, and a catalog
 * refetch hands down a fresh resolver.
 *
 * So the component below is defined ONCE, at module scope, and reads the
 * resolver here. Identity of the resolver is then not identity of the
 * component, which is the property the fallback depends on.
 */
const ImageResolverContext = createContext<MarkdownImageResolver | null>(null);

const MarkdownImage: Components["img"] = function MarkdownImage({
  src,
  alt,
  node: _node,
  ...rest
}) {
  const resolveImage = useContext(ImageResolverContext);
  const reference = typeof src === "string" ? src : "";
  // A reference with NO ADDRESS is nobody's to resolve. react-markdown runs
  // `defaultUrlTransform` on `src` before this override sees it, and its
  // allowlist is http(s)/mailto/xmpp/irc — so a `data:` image (and any other
  // scheme) arrives here as "", exactly like an authored `![x]()`. Asking a
  // resolver about it would mint a broken-reference marker naming nothing;
  // declining keeps the ticket's promise that a `data:` URL renders exactly as
  // it does today.
  const resolution =
    resolveImage && reference !== "" ? resolveImage(reference) : null;
  // `null` is "not mine": emit what react-markdown would have emitted, so an
  // absolute external image is byte-identical to the pre-LAT-2081 render.
  if (!resolution) return <img src={src} alt={alt} {...rest} />;
  if (resolution.kind === "broken") {
    return <BrokenImageReference alt={alt ?? ""} path={resolution.path} />;
  }
  return <ResolvedImage resolution={resolution} alt={alt ?? ""} />;
};

export function MarkdownView({
  children,
  className,
  remarkPlugins,
  components,
  resolveImage,
  transformLinkUrl,
}: {
  children: string;
  className?: string;
  /** Extra remark plugins, run after remark-gfm in the order given. */
  remarkPlugins?: RemarkPlugins;
  /** Element overrides handed to react-markdown. Omit for bare anchors. */
  components?: Components;
  /** LAT-2081 — decide what one image reference renders as. Omit and every
   *  reference stays the plain `<img>` react-markdown emits (today's
   *  behaviour, and what a surface without KB context should show). */
  resolveImage?: MarkdownImageResolver;
  /** Optional surface-owned rewrite for safe anchor destinations. */
  transformLinkUrl?: (url: string) => string;
}) {
  // The caller's own `components` is applied LAST so an explicit `img`
  // override still wins — this is a default, not a closed door. This object is
  // rebuilt every render and that is fine: what react-markdown reconciles on is
  // the element TYPE inside it, and `MarkdownImage` is module-level, so the
  // type never changes. (Memoising HERE could not have helped: the caller's own
  // `components` is itself a fresh object every render.)
  const resolvedComponents: Components | undefined =
    resolveImage || components
      ? {
          ...(resolveImage ? { img: MarkdownImage } : {}),
          ...components,
        }
      : undefined;
  return (
    <ImageResolverContext.Provider value={resolveImage ?? null}>
      <div
        className={cn(
          "prose prose-sm dark:prose-invert max-w-none",
          "prose-pre:overflow-x-auto",
          "prose-code:rounded prose-code:bg-muted/60 prose-code:px-1 prose-code:py-0.5 prose-code:font-mono prose-code:text-[0.85em] prose-code:before:content-[''] prose-code:after:content-['']",
          className
        )}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
          components={resolvedComponents}
          urlTransform={(url, key) => {
            const safe = defaultUrlTransform(url);
            return key === "href" && transformLinkUrl
              ? transformLinkUrl(safe)
              : safe;
          }}
        >
          {children}
        </ReactMarkdown>
      </div>
    </ImageResolverContext.Provider>
  );
}
